import { createContext, useContext, useEffect, useState } from 'react'
import type { MessageKey } from '../i18n'

/**
 * Périmètre partagé par les écrans : pays et période choisis dans la barre du haut,
 * libellé de fin de fil d'Ariane posé par une page de détail.
 */
interface ShellState {
  /** '' = tous les pays du périmètre de l'utilisateur. */
  country: string
  setCountry: (c: string) => void
  /** Période en jours. */
  period: number
  setPeriod: (d: number) => void
  crumb: string | null
  setCrumb: (label: string | null) => void
  /** Afficher les données de démonstration (sites factices) dans les écrans Paris & jeux. */
  showFixtures: boolean
  setShowFixtures: (v: boolean) => void
}

const ShellContext = createContext<ShellState | null>(null)

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* stockage indisponible : le choix vaut pour la session seulement */
  }
}

export function ShellProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountryState] = useState(() => readStored('icc_shell_country') ?? '')
  const [period, setPeriodState] = useState(() => Number(readStored('icc_shell_period')) || 30)
  const [crumb, setCrumb] = useState<string | null>(null)
  const [showFixtures, setShowFixturesState] = useState(() => readStored('icc_show_fixtures') === '1')

  const setCountry = (c: string) => {
    setCountryState(c)
    writeStored('icc_shell_country', c)
  }
  const setPeriod = (d: number) => {
    setPeriodState(d)
    writeStored('icc_shell_period', String(d))
  }

  const setShowFixtures = (v: boolean) => {
    setShowFixturesState(v)
    writeStored('icc_show_fixtures', v ? '1' : '0')
  }

  return (
    <ShellContext.Provider
      value={{ country, setCountry, period, setPeriod, crumb, setCrumb, showFixtures, setShowFixtures }}
    >
      {children}
    </ShellContext.Provider>
  )
}

export function useShell(): ShellState {
  const ctx = useContext(ShellContext)
  if (!ctx) throw new Error('useShell doit être utilisé dans ShellProvider')
  return ctx
}

/** Pose le dernier maillon du fil d'Ariane le temps que la page est affichée. */
export function useCrumb(label: string | null) {
  const { setCrumb } = useShell()
  useEffect(() => {
    setCrumb(label)
    return () => setCrumb(null)
  }, [label, setCrumb])
}

/** Bornes de dates (AAAA-MM-JJ) correspondant à la période choisie. */
export function periodRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: iso(from), to: iso(to) }
}

export const PERIOD_OPTIONS: Array<{ value: number; labelKey: MessageKey }> = [
  { value: 7, labelKey: 'period.days7' },
  { value: 30, labelKey: 'period.days30' },
  { value: 90, labelKey: 'period.months3' },
  { value: 365, labelKey: 'period.months12' },
]
